import { Link, useLocation } from 'wouter';
import { LegalLinks } from '../LegalLinks';
import { PRIMARY_NAV, SUPPORT_EMAIL, disclaimerForPath } from '../../lib/shell/navigation';
import { cn } from '../../lib/utils';
import { BrandMark } from './BrandMark';

/**
 * The global footer, rendered by both shells.
 *
 * V2-018C-2 brings this to legacy parity. The legacy footer is a single row —
 * brand mark, the safety disclaimer, then `Why HPOS · Privacy Policy · Terms of
 * Service · Support` — stacking on small screens. The previous version added an
 * "Assessments" column that legacy does not have; it is removed here, because
 * migration copies the legacy footer rather than improving on it.
 *
 * `LegalLinks` is reused rather than re-implemented. It already carries the
 * `legal-links` anchors the delivered suites assert, and forking those links to
 * gain a separator style would trade a real regression risk for a cosmetic
 * difference.
 *
 * The marketing destination (`Why HPOS`) is confined to the public shell: it is
 * registered in `PUBLIC_PATHS` before its page exists, so advertising it from a
 * delivered application page would offer a dead link. Restore it to both shells
 * when the `/why` slice lands.
 */
export type SiteFooterProps = {
  /** `public` widens the footer to the Experience Shell's measure. */
  variant: 'public' | 'app';
};

export function SiteFooter({ variant }: SiteFooterProps) {
  // The legacy disclaimer wording varies by page; see `disclaimerForPath`.
  const [location] = useLocation();
  const disclaimer = disclaimerForPath(location);

  const showMarketingNav = variant === 'public';

  return (
    <footer
      data-testid="site-footer"
      className={cn('mt-auto border-t border-border px-4', variant === 'public' ? 'py-8' : 'pt-4')}
    >
      <div
        className={cn(
          'flex w-full flex-col items-center justify-between gap-4 md:flex-row',
          variant === 'public' ? 'mx-auto max-w-6xl' : '',
        )}
      >
        <BrandMark placement="footer" />

        {/* `null` on /perspective, whose legacy footer carries no disclaimer.
            Omitted rather than emptied, so the element is absent exactly as it
            is in legacy. */}
        {disclaimer ? (
          <p
            data-testid="safety-disclaimer"
            className="max-w-lg text-center text-xs text-muted-foreground"
          >
            {disclaimer}
          </p>
        ) : null}

        <nav
          aria-label="Footer"
          className="flex items-center gap-5 text-xs text-muted-foreground"
        >
          {showMarketingNav
            ? PRIMARY_NAV.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {item.label}
                </Link>
              ))
            : null}
          <LegalLinks />
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="footer-support"
            className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            Support
          </a>
        </nav>
      </div>
    </footer>
  );
}
